import { Record } from "immutable"
import { subDays, addMonths, format, startOfMonth } from "date-fns"

import { prepareSearch, fetchFlights } from "common/services/search/fetchFlightsApi"
import SearchDate from "../../records/SearchDate"
import { DIRECTIONS, FORM_FIELDS, CHANGE_TYPES } from "../../consts/form"
import { DATE_FORMAT, DATE_MODES } from "../../consts/date"
import { PLACE_VALUE_MODES } from "../../consts/place"
import PickPrices from "../../records/PickPrices"
import MonthPrices from "../../records/MonthPrices"

const { OUTBOUND, INBOUND } = DIRECTIONS

export const PricesRecord = Record({
  outbound: new PickPrices(),
  inbound: new PickPrices(),
  error: null,
})

// utils
// ===

/**
 * @param origin {SearchPlace}
 * @param destination {SearchPlace}
 * @param direction {String} outbound or inbound
 * @param {bool} enableHiddenFeatures
 * @returns {bool} result of the check
 */
export function canHavePrices(origin, destination, direction, enableHiddenFeatures = false) {
  if (enableHiddenFeatures) {
    return (
      origin.mode !== PLACE_VALUE_MODES.TEXT &&
      destination.mode !== PLACE_VALUE_MODES.TEXT &&
      (direction === OUTBOUND ||
        direction === INBOUND ||
        (origin.isSimple() && destination.isSimple()))
    )
  }
  return (
    origin.mode !== PLACE_VALUE_MODES.TEXT &&
    destination.mode !== PLACE_VALUE_MODES.TEXT &&
    (direction === OUTBOUND || (origin.isSimple() && destination.isSimple()))
  )
}

/**
 * @param form {PricesRecord}
 * @returns {PricesRecord} - clean form
 */
export function clearMonths(form) {
  return form
    .set(OUTBOUND, new PickPrices())
    .set(INBOUND, new PickPrices())
    .set("error", null)
}

/**
 * @param form {PricesRecord}
 * @param field - one of FORM_FIELDS
 * @param type - one of CHANGE_TYPES
 * @returns {PricesRecord} - clean form
 */
export function clearMonthsLazy(form, field, type) {
  if (
    type === CHANGE_TYPES.SELECT &&
    (field === FORM_FIELDS.ORIGIN || field === FORM_FIELDS.DESTINATION)
  ) {
    return clearMonths(form)
  }

  return form
}

// functions
// ===

/**
 * @param origin {string | MultiPlace}
 * @param destination {string | MMultiPlace}
 * @param direction {String} outbound or inbound
 * @param date - the date to load
 * @returns {Promise} - resolving the required dates
 */
export function loadPrices(origin, destination, direction, date) {
  const from = format(startOfMonth(date), DATE_FORMAT)
  const to = format(subDays(addMonths(from, 1), 1), DATE_FORMAT)

  const month = new SearchDate({
    mode: DATE_MODES.INTERVAL,
    from,
    to,
  })

  const search = prepareSearch({
    origin: direction === OUTBOUND ? origin : destination,
    destination: direction === OUTBOUND ? destination : origin,
    outboundDate: month,
    onePerDay: true,
  })

  return fetchFlights(search).then(res => res && res.data)
}

/**
 * @param form {PricesRecord}
 * @param origin {MultiPlace}
 * @param destination {MultiPlace}
 * @param direction {String} outbound or inbound
 * @param monthId {String} "YYYY-MM"
 * @param {bool} enableHiddenFeatures
 * @returns {Object: {change<PricesRecord>, update<bool>}} - updated form and need to change
 */
export function getPricesLoadingChange(
  form,
  origin,
  destination,
  direction,
  monthId,
  enableHiddenFeatures = false,
) {
  let change = form
  let update = false
  if (!change.get(direction).getMonth(monthId)) {
    if (canHavePrices(origin, destination, direction, enableHiddenFeatures)) {
      update = true

      change = change.set(
        direction,
        change.get(direction).addMonth(monthId, new MonthPrices({ loading: true })),
      )
    } else {
      change = change.set(direction, new PickPrices({ unavailable: true }))
    }
  }

  return { change, update }
}

/**
 * @param form {PricesRecord}
 * @param origin {SearchPlace}
 * @param destination {SearchPlace}
 * @param direction {String} outbound or inbound
 * @param monthId {String} "YYYY-MM"
 * @param success {Function} flights => {}
 * @param fail {Function} err => {}
 * @param {bool} enableHiddenFeatures default false
 * @returns {PricesRecord} - updated form
 */
export function loadPricesFull(
  form,
  origin,
  destination,
  direction,
  monthId,
  success,
  fail,
  enableHiddenFeatures = false,
) {
  const { update, change } = getPricesLoadingChange(
    form,
    origin,
    destination,
    direction,
    monthId,
    enableHiddenFeatures,
  )

  if (update) {
    loadPrices(origin, destination, direction, monthId)
      .then(success)
      .catch(fail)
  }

  return change
}

/**
 * @param form {PricesRecord}
 * @param direction {String} outbound or inbound
 * @param monthId {String} "YYYY-MM"
 * @returns {PricesRecord} - updated form
 */
export function pricesLoadingRequest(form, direction, monthId) {
  return form.set(
    direction,
    form.get(direction).addMonth(monthId, new MonthPrices({ loading: true })),
  )
}

/**
 * @param form {PricesRecord}
 * @param direction {String} outbound or inbound
 * @param monthId {String} "YYYY-MM"
 * @param flights {Array<flight>} flights from this.loadPrices (or FlightsAPI)
 * @returns {PricesRecord} - updated form
 */
export function pricesLoadingSuccess(form, direction, monthId, flights) {
  return form
    .setIn([direction, "months", monthId, "loading"], false)
    .set(direction, form.get(direction).addMonthFlights(monthId, flights))
}

/**
 * @param form {PricesRecord}
 * @param direction {String} outbound or inbound
 * @param monthId {String} "YYYY-MM"
 * @param err {Error} the error of the loading
 * @returns {PricesRecord} - updated form
 */
export function pricesLoadingFailed(form, direction, monthId, err) {
  return form.setIn([direction, "months", monthId, "loading"], false).set("error", err)
}

// getters
// ===

/**
 * @param month
 * @param year
 * @returns - "YYYY-MM"
 */
export function getMonthId(month, year) {
  return MonthPrices.getId(month, year)
}
